/**
 * Maps backend feature keys (apps/tsbackend franchisee.routes AVAILABLE_FEATURES)
 * to URL path prefixes franchisee admins may access when the feature is granted.
 */
import {
  FRANCHISEE_BUSINESS_TYPES,
  BUSINESS_TYPE_BY_VALUE,
} from './franchiseeBusinessTypes';

export const FRANCHISE_FEATURE_ROUTE_PREFIXES = {
  // Keep /bills/cart-model under smartbill for existing admins who already have access.
  // View Bills is included with any bill-creation module — history is required to use billing.
  smartbill: [
    '/bills/create',
    '/bills/drafts',
    '/bills/cart-model',
    '/bills/admin-controls',
    '/bills/view',
    '/bills/advance-bills',
    '/shop-orders',
  ],
  cart_model_bill: ['/bills/cart-model', '/bills/view', '/bills/advance-bills', '/shop-orders'],
  view_bills: ['/bills/view', '/bills/advance-bills'],
  service_management: ['/bills/services'],
  coupon_management: [
    '/coupons',
    '/bills/post-bill-coupons',
    '/shop-orders',
  ],
  counter_stats: ['/bills/counter-sales-datewise', '/bills/service-charges'],
  customer_search: ['/customer/search', '/customer/dashboard'],
  pending_works: ['/bills/pending-works'],
  enquiries: ['/bills/enquiries', '/bills/enquiry-form', '/bills/operator-calls'],
  job_alerts: ['/job-alerts', '/jalertsmngmt', '/application-chat'],
  lms_management: [
    '/dashboard/students',
    '/dashboard/students/courses',
    '/dashboard/students/course-referrals',
    '/dashboard/training-programs',
    '/dashboard/students/courses/:courseId/builder',
    '/dashboard/students/courses/:courseId/display-settings',
    '/dashboard/students/flashcards',
    '/dashboard/students/assignments/review',
    '/dashboard/students/quizzes/review',
    '/dashboard/students/coding/problems',
    '/dashboard/students/coding/review',
  ],
  online_works: ['/online-works'],
  ads_management: ['/locsy/ads', '/franchisee/ads'],
  citizen_requirements: ['/franchisee/citizen-requirements'],
  reports_analytics: [],
  knls_services: ['/services'],
  knls_courses: ['/courses', '/training-programs', '/terms-and-conditions'],
  operator_management: ['/business-admin/my-operators', '/locsy/operators'],
  whatsapp_config: ['/bills/whatsapp-config'],
};

export const FRANCHISE_FEATURE_CATALOG = [
  {
    key: 'smartbill',
    label: 'Smart Bill (QR)',
    description: 'Create QR based smart bills. Cart Model Bill remains included for old Smart Bill grants.',
    keywords: ['smart bill', 'qr bill', 'bill create', 'billing'],
  },
  {
    key: 'cart_model_bill',
    label: 'Cart Model Bill',
    description: 'Cart-style billing with quantity, split payment, QR and local draft bills included.',
    keywords: ['cart model bill', 'cart bill', 'draft bills', 'billing'],
  },
  {
    key: 'view_bills',
    label: 'View Bills',
    description:
      'Monitor, search, share, edit and manage bill history. Included free with Smart Bill or Cart Model Bill.',
    keywords: ['view bills', 'bill history', 'bill analytics'],
  },
  {
    key: 'service_management',
    label: 'Service Management',
    description: 'Manage billing services, service charges and challans.',
    keywords: ['services', 'service crud', 'charges', 'challans'],
  },
  {
    key: 'coupon_management',
    label: 'Coupon Management',
    description: 'Create, issue and manage coupons.',
    keywords: ['coupons', 'post bill coupons', 'issued coupons'],
  },
  {
    key: 'counter_stats',
    label: 'Counter Stats',
    description: 'Counter sales and service charge analytics.',
    keywords: ['counter stats', 'sales', 'service charges'],
  },
  {
    key: 'customer_search',
    label: 'Customer Search',
    description: 'Search customer history and open customer dashboards.',
    keywords: ['customer search', 'customer history', 'customer dashboard'],
  },
  {
    key: 'pending_works',
    label: 'Pending Works',
    description: 'Track pending and in-progress work.',
    keywords: ['pending works', 'work status'],
  },
  {
    key: 'enquiries',
    label: 'Enquiries / Application Chat',
    description: 'Call leads, enquiries, operator calls and application chat.',
    keywords: ['enquiries', 'call leads', 'application chat', 'operator calls'],
  },
  {
    key: 'job_alerts',
    label: 'Job Alerts',
    description: 'Manage job alerts and related customer workflows.',
    keywords: ['job alerts', 'jobs'],
  },
  {
    key: 'lms_management',
    label: 'LMS / Student Management',
    description: 'Students, courses, builders, flashcards and training programs.',
    keywords: ['lms', 'students', 'courses', 'training', 'flashcards'],
  },
  {
    key: 'online_works',
    label: 'Online Works',
    description: 'Manage online work modules.',
    keywords: ['online works'],
  },
  {
    key: 'ads_management',
    label: 'Ads Management',
    description: 'Publish and manage business advertisements.',
    keywords: ['ads', 'advertisements'],
  },
  {
    key: 'citizen_requirements',
    label: 'Citizen Requirements Inbox',
    description: 'View citizen posts matched to your business categories and send offers.',
    keywords: ['citizen requirements', 'user postings', 'enquiries'],
  },
  {
    key: 'reports_analytics',
    label: 'Reports / Analytics',
    description: 'Backup, reports and analytics access.',
    keywords: ['reports', 'analytics', 'backup'],
  },
  {
    key: 'knls_services',
    label: 'KNLS Services',
    description: 'Public KNLS services catalog.',
    keywords: ['knls services', 'services catalog'],
  },
  {
    key: 'knls_courses',
    label: 'KNLS Courses',
    description: 'Public KNLS courses and training programs.',
    keywords: ['knls courses', 'training programs', 'terms'],
  },
  {
    key: 'operator_management',
    label: 'Operator Management',
    description: 'Create and manage business operators.',
    keywords: ['operators', 'staff management'],
  },
  {
    key: 'whatsapp_config',
    label: 'WhatsApp Message Config',
    description: 'Customize WhatsApp messages with your shop name, phone and address.',
    keywords: ['whatsapp', 'message config', 'templates'],
  },
  {
    key: 'export_catalog',
    label: 'Export Catalog',
    description:
      'Allow this Mitra to Share / push products into category catalog templates (e.g. Kirana defaults). Ops-only — no registration fee. Regular shops still Import/Update only.',
    keywords: [
      'export catalog',
      'share catalog',
      'catalog templates',
      'beta',
      'kirana template',
      'contribute',
    ],
  },
];

/** Default SaaS features per registration template (sync with tsbackend franchisee.routes). */
export const BUSINESS_TYPE_FEATURE_TEMPLATES = {
  online_center: [
    'smartbill',
    'cart_model_bill',
    'view_bills',
    'service_management',
    'coupon_management',
    'counter_stats',
    'customer_search',
    'pending_works',
    'enquiries',
    'job_alerts',
    'online_works',
    'ads_management',
    'reports_analytics',
    'knls_services',
    'operator_management',
  ],
  education: [
    'smartbill',
    'cart_model_bill',
    'view_bills',
    'service_management',
    'coupon_management',
    'counter_stats',
    'customer_search',
    'pending_works',
    'enquiries',
    'lms_management',
    'knls_courses',
    'ads_management',
    'reports_analytics',
    'operator_management',
  ],
  service_general: [
    'smartbill',
    'cart_model_bill',
    'view_bills',
    'service_management',
    'coupon_management',
    'counter_stats',
    'customer_search',
    'pending_works',
    'enquiries',
    'ads_management',
    'reports_analytics',
    'operator_management',
  ],
  product_retail: [
    'smartbill',
    'cart_model_bill',
    'view_bills',
    'coupon_management',
    'counter_stats',
    'customer_search',
    'ads_management',
    'reports_analytics',
    'operator_management',
  ],
};

const buildBusinessTypeFeatureMap = () => {
  const map = {};
  for (const entry of FRANCHISEE_BUSINESS_TYPES) {
    const template = entry.template || 'service_general';
    map[entry.value] = BUSINESS_TYPE_FEATURE_TEMPLATES[template] || BUSINESS_TYPE_FEATURE_TEMPLATES.service_general;
  }
  return map;
};

export const getDefaultFeaturesForBusinessType = (businessType) => {
  const key = normalizeBusinessTypeKey(businessType);
  const entry = BUSINESS_TYPE_BY_VALUE[key];
  if (entry?.template && BUSINESS_TYPE_FEATURE_TEMPLATES[entry.template]) {
    return BUSINESS_TYPE_FEATURE_TEMPLATES[entry.template];
  }
  return BUSINESS_TYPE_FEATURE_TEMPLATES.service_general;
};

/** Union of default features for multiple business categories (e.g. Kirana + Xerox). */
export const getMergedFeaturesForBusinessTypes = (businessTypes = []) => {
  const merged = new Set();
  for (const raw of businessTypes) {
    getDefaultFeaturesForBusinessType(raw).forEach((f) => merged.add(f));
  }
  return [...merged];
};

/** Uses API-loaded category options (template field) when available. */
export const getMergedFeaturesForCategorySelection = (
  businessTypes = [],
  categoryOptions = [],
) => {
  const optionMap = new Map(categoryOptions.map((o) => [o.value, o]));
  const merged = new Set();
  for (const value of businessTypes) {
    const opt = optionMap.get(value);
    if (opt?.template && BUSINESS_TYPE_FEATURE_TEMPLATES[opt.template]) {
      BUSINESS_TYPE_FEATURE_TEMPLATES[opt.template].forEach((f) => merged.add(f));
    } else {
      getDefaultFeaturesForBusinessType(value).forEach((f) => merged.add(f));
    }
  }
  return [...merged];
};

export const BUSINESS_TYPE_RELEVANT_FEATURES = {
  ...buildBusinessTypeFeatureMap(),
  online_center: [
    'smartbill',
    'cart_model_bill',
    'view_bills',
    'service_management',
    'coupon_management',
    'counter_stats',
    'customer_search',
    'pending_works',
    'enquiries',
    'online_works',
    'ads_management',
    'reports_analytics',
    'knls_services',
    'operator_management',
  ],
  computer_training_center: [
    'smartbill',
    'cart_model_bill',
    'view_bills',
    'service_management',
    'coupon_management',
    'counter_stats',
    'customer_search',
    'pending_works',
    'enquiries',
    'lms_management',
    'knls_courses',
    'ads_management',
    'reports_analytics',
    'operator_management',
  ],
  coaching_center: [
    'smartbill',
    'cart_model_bill',
    'view_bills',
    'service_management',
    'coupon_management',
    'counter_stats',
    'customer_search',
    'pending_works',
    'enquiries',
    'lms_management',
    'knls_courses',
    'ads_management',
    'reports_analytics',
    'operator_management',
  ],
  college: BUSINESS_TYPE_FEATURE_TEMPLATES.education,
};

export const normalizeBusinessTypeKey = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');

export const getFeatureMeta = (featureKey) => {
  const key = String(featureKey || '').trim();
  const known = FRANCHISE_FEATURE_CATALOG.find((feature) => feature.key === key);
  if (known) return known;

  return {
    key,
    label: key
      .split('_')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' '),
    description: 'New dashboard feature. Review and decide whether this business admin needs access.',
    keywords: [key],
  };
};

export const getMergedFranchiseFeatureCatalog = (backendFeatureKeys = []) => {
  const keys = [
    ...FRANCHISE_FEATURE_CATALOG.map((feature) => feature.key),
    ...Object.keys(FRANCHISE_FEATURE_ROUTE_PREFIXES),
    ...(Array.isArray(backendFeatureKeys) ? backendFeatureKeys : []),
  ];

  return Array.from(new Set(keys.filter(Boolean))).map(getFeatureMeta);
};

/** Paths any franchisee admin can open (no feature flag). */
export const FRANCHISEE_BASE_PATH_PREFIXES = [
  '/locsy/dashboard',
  '/locsy/accounts',
  '/locsy/ads',
  '/locsy/products',
  // Core business-media page: always available like its dashboard Quick Action.
  '/locsy/shop-photos',
  '/locsy/admin/login',
  '/locsy/admin/register',
  '/locsy/admin/forgot-password',
  '/locsy/admin/forgot-pin',
  '/franchisee/dashboard',
  '/franchisee/admin/login',
  '/franchisee/admin/register',
  '/franchisee/admin/forgot-password',
  '/locsydashboard',
  '/locsydashboard/login',
  '/locsydashboard/register',
  '/locsydashboard/forgot-password',
  '/locsydashboard/forgot-pin',
  '/locsydashboard/dashboard',
  '/profile',
  '/bills/whatsapp-config',
];

/**
 * @param {string} pathname
 * @param {string[]} features enabled feature keys from login (array of feature name strings)
 */
export function pathnameAllowedByFranchiseFeatures(pathname, features) {
  const list = Array.isArray(features) ? features : [];
  if (
    FRANCHISEE_BASE_PATH_PREFIXES.some(
      (p) => pathname === p || pathname.startsWith(`${p}/`),
    )
  ) {
    return true;
  }
  for (const featureKey of list) {
    if (!featureKey) continue;

    const prefixes = FRANCHISE_FEATURE_ROUTE_PREFIXES[featureKey];
    if (!prefixes) continue;
    for (const prefix of prefixes) {
      if (pathname === prefix || pathname.startsWith(`${prefix}/`)) {
        return true;
      }
    }
  }
  return false;
}
